import {
  Component,
  DestroyRef,
  DOCUMENT,
  RESPONSE_INIT,
  computed,
  inject,
  signal,
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Params, Router, RouterLink } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

import {
  armarLinkWhatsApp,
  esUrlRenderizable,
  FuentesFoto,
  fuentesFoto,
  ImagenPublica,
  Operacion,
  partesPrecio,
  precioPrincipal,
  PropiedadDetalle,
  PropiedadPublica,
  TIPO_PROPIEDAD_SINGULAR,
  textoODefault,
} from '../../core/models/propiedad.model';
import { PropiedadesPublicoService } from '../../core/services/propiedades-publico.service';
import { OpenGraphService } from '../../core/services/open-graph.service';
import { ZonaMapa } from './zona-mapa';
import { FichaPdf } from './ficha-pdf';
import { FotoPropiedad } from '../../shared/foto-propiedad/foto-propiedad';
import { Icono } from '../../shared/icono/icono';
import { indiceConWrap, SwipeHorizontal } from '../../shared/swipe-horizontal';

/**
 * Respuestas de `GET /publico/propiedades/:id` que significan "esta ficha no
 * existe": 404 (inexistente o no publicada) y 400, que es lo que
 * devuelve la API con un id que no es un UUID.
 */
const STATUS_FICHA_INEXISTENTE = new Set([400, 404]);

/** Celdas de la grilla de miniaturas del Figma (`244:3078`): 2×2. */
const CELDAS_GRILLA = 4;

/**
 * Query param ("sin contacto") que agrega el Backoffice al compartir por
 * WhatsApp una propiedad de una alianza (FE-126, #17): con él la ficha no
 * muestra el botón de WhatsApp ni el logo de la inmobiliaria, y el PDF sale
 * sin los datos del broker. Alcanza con que esté presente (`?sc`, `?sc=1`); a
 * propósito es sólo un query param, no un token (definición del cliente).
 */
export const PARAM_SIN_CONTACTO = 'sc';

/*
 * Escalones del bloque de precio.
 *
 * - hasta 12 caracteres → `Display 2` (36 px), como el Figma.
 * - hasta 15 → 30 px, que es lo que entra en 238 px con ese largo.
 * - más → 24 px (`Title`); en la práctica sólo cae acá una línea como
 *   `U$S 2.200.000 /mes`, de 18.
 */
const LARGO_MAX_DISPLAY = 12;
const LARGO_MAX_MEDIO = 15;

/** Miniatura de la grilla, con su posición dentro de `galeria()`. */
interface Miniatura {
  indice: number;
  url: string;
  fuentes: FuentesFoto;
}

/**
 * Ficha pública de la propiedad (Figma node 18-851; FE-06):
 *
 * - Título "Tipo, Localidad" + acciones Video / Descargar / Compartir.
 * - Galería: foto principal de 384 px + grilla 2×2 de miniaturas de 180 px
 *   (Figma `244:3078`), con el botón de ampliar sobre la principal.
 * - Descripción, Mt2/Ambientes, links de video (Instagram/TikTok/YouTube),
 *   precio y consulta por WhatsApp.
 * - Zona: dirección en texto + mapa geocodificado (spec §7.1 — sin iframe
 *   ni link manual).
 *
 * Carga el detalle de `GET /publico/propiedades/:id`; la tarjeta del router
 * state sirve de vista previa instantánea, con sus fotos incluidas: desde la
 * grilla eso ya es la galería completa, así que las miniaturas aparecen antes
 * de que responda el detalle. La viñeta de contacto muestra el
 * logo del broker titular y consulta por WhatsApp al agente asignado
 * (definición de negocio).
 */
@Component({
  selector: 'app-ficha-propiedad',
  host: { '(document:keydown)': 'tecla($event)' },
  imports: [
    RouterLink,
    ZonaMapa,
    Icono,
    FotoPropiedad,
    FichaPdf,
    SwipeHorizontal,
  ],
  templateUrl: './ficha-propiedad.html',
  styleUrl: './ficha-propiedad.scss',
})
export class FichaPropiedad {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly service = inject(PropiedadesPublicoService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly openGraph = inject(OpenGraphService);
  private readonly document = inject(DOCUMENT);
  /**
   * Respuesta HTTP de la página: sólo existe en el render del servidor. Ahí
   * una ficha inexistente sale con 404 en vez de 200 con la ficha vacía, para
   * que los buscadores no la indexen.
   */
  private readonly responseInit = inject(RESPONSE_INIT, { optional: true });

  readonly detalle = signal<PropiedadDetalle | null>(null);
  readonly tarjetaPrevia = signal<PropiedadPublica | null>(null);
  /**
   * Fotos que traía la tarjeta de origen, para pintar la galería sin esperar
   * el detalle. Desde la grilla vienen las N (BP-03); desde el pin del mapa,
   * sólo la portada.
   */
  private readonly fotosPrevias = signal<ImagenPublica[]>([]);
  readonly cargando = signal(true);
  readonly fotoActiva = signal(0);

  readonly propiedad = computed(
    () => this.detalle() ?? this.tarjetaPrevia(),
  );

  /**
   * Fotos de la galería con sus variantes (FP-28), por orden de preferencia:
   * `galeria` del detalle, que las trae; `imagenes` como respaldo plano por si
   * una respuesta viniera sin ella; las fotos que mandó la pantalla anterior
   * mientras el detalle está en vuelo; y la portada como último recurso.
   */
  readonly galeria = computed<ImagenPublica[]>(() => {
    const detalle = this.detalle();
    if (detalle?.galeria?.length) return detalle.galeria;
    if (detalle?.imagenes.length) return detalle.imagenes.map((url) => ({ url }));

    const previas = this.fotosPrevias();
    if (previas.length) return previas;

    const previa = this.propiedad();
    return previa?.imagenPortada
      ? [{ url: previa.imagenPortada, variantes: previa.imagenPortadaVariantes }]
      : [];
  });

  /**
   * La propiedad tiene al menos una foto. Con `false` la ficha no monta la
   * galería: se evita el placeholder grande con cuatro celdas vacías al lado.
   */
  readonly tieneFotos = computed(() => this.galeria().length > 0);

  readonly fotoPrincipal = computed(
    () => this.galeria()[this.fotoActiva()] ?? null,
  );

  /** Foto grande: `card` (800 px) alcanza para la celda principal. */
  readonly fuentesPrincipal = computed<FuentesFoto | null>(() => {
    const foto = this.fotoPrincipal();
    return foto ? fuentesFoto(foto, 'card') : null;
  });

  /**
   * Miniaturas con sus fuentes ya resueltas en `thumb` (400 px).
   *
   * Va en un `computed` y no en un método llamado desde la plantilla: un
   * método devuelve un objeto nuevo en cada ciclo de detección, y eso hace
   * que `app-foto-propiedad` vea su input cambiado siempre. Como el
   * componente resetea su estado de error cuando cambia `fuentes`, una
   * miniatura rota nunca llegaba a mostrar el placeholder.
   */
  private readonly miniaturas = computed(() =>
    this.galeria().map((foto, indice) => ({
      indice,
      url: foto.url,
      fuentes: fuentesFoto(foto, 'thumb'),
    })),
  );

  /**
   * Celdas de la grilla 2×2 del Figma (`244:3078`): las cuatro fotos que no
   * son la principal, y `null` en las que sobran cuando el aviso tiene menos
   * de cinco. La grilla se dibuja siempre completa —las celdas vacías quedan
   * en gris— para que el bloque no cambie de forma según cuántas fotos haya.
   *
   * Deriva de `miniaturas()` en vez de recalcular las fuentes: así las que
   * siguen en la grilla mantienen la misma referencia de `fuentes` al cambiar
   * de foto principal y no se les resetea el estado de error.
   */
  readonly celdasGrilla = computed<(Miniatura | null)[]>(() => {
    const activa = this.fotoActiva();
    const otras = this.miniaturas()
      .filter((mini) => mini.indice !== activa)
      .slice(0, CELDAS_GRILLA);
    return [
      ...otras,
      ...Array.from({ length: CELDAS_GRILLA - otras.length }, () => null),
    ];
  });

  readonly titulo = computed(() => {
    const p = this.propiedad();
    return p ? TIPO_PROPIEDAD_SINGULAR[p.tipoPropiedad] : '';
  });

  /** "Tipo, Localidad — Phindi": lo usan Compartir y el preview (FP-31). */
  readonly tituloCompartir = computed(
    () => `${this.titulo()}, ${this.propiedad()?.localidad ?? ''} — Phindi`,
  );

  /** Moneda e importe por separado: en el Figma llevan pesos distintos. */
  readonly precioVentaPartes = computed(() => {
    const p = this.propiedad();
    return p && p.operacion !== Operacion.ALQUILER && p.precioVenta != null
      ? partesPrecio(p.precioVenta, p.monedaVenta)
      : null;
  });

  readonly precioAlquilerPartes = computed(() => {
    const p = this.propiedad();
    return p && p.operacion !== Operacion.VENTA && p.precioAlquiler != null
      ? partesPrecio(p.precioAlquiler, p.monedaAlquiler)
      : null;
  });

  readonly precioVenta = computed(() => {
    const partes = this.precioVentaPartes();
    return partes ? `${partes.simbolo} ${partes.monto}` : null;
  });

  readonly precioAlquiler = computed(() => {
    const partes = this.precioAlquilerPartes();
    return partes ? `${partes.simbolo} ${partes.monto}` : null;
  });

  /** Alquiler tal como se muestra, con el período incluido. */
  readonly precioAlquilerTexto = computed(() => {
    const alquiler = this.precioAlquiler();
    return alquiler ? `${alquiler} /mes` : null;
  });

  /** Líneas del bloque de precio, tal como se muestran. */
  private readonly lineasPrecio = computed(() =>
    [this.precioVenta(), this.precioAlquilerTexto()].filter(
      (texto): texto is string => texto !== null,
    ),
  );

  /**
   * Cuerpo del precio según lo que haya que entrar (ver los escalones de
   * arriba). Con dos precios nunca se usa `Display 2`: son dos renglones en un
   * bloque de 100 px.
   */
  readonly tamanioPrecio = computed<'display' | 'medio' | 'compacto'>(() => {
    const lineas = this.lineasPrecio();
    const largo = Math.max(0, ...lineas.map((linea) => linea.length));
    if (largo > LARGO_MAX_MEDIO) return 'compacto';
    if (largo > LARGO_MAX_DISPLAY || lineas.length > 1) return 'medio';
    return 'display';
  });

  /** Dirección completa de la sección Zona. */
  readonly direccionCompleta = computed(() => {
    const p = this.propiedad();
    return p ? `${p.direccion}, ${p.localidad}` : '';
  });

  /**
   * La ficha llegó por un link compartido de una alianza (#17): se ocultan el
   * botón de WhatsApp y el logo de la inmobiliaria, y el PDF sale sin el
   * contacto del broker.
   */
  readonly ocultarContacto =
    this.route.snapshot.queryParamMap.has(PARAM_SIN_CONTACTO);

  readonly linkWhatsApp = computed(() => {
    const p = this.propiedad();
    if (!p) return '';
    // `document.location` y no el global `location`: la ficha se renderiza en
    // el servidor, donde no hay `window`.
    const texto = `Hola! Me interesa esta propiedad publicada en Phindi: ${p.direccion}, ${p.localidad} (${precioPrincipal(p)}) — ${this.document.location.href}`;
    return armarLinkWhatsApp(p.contacto?.whatsapp, texto);
  });

  /** Logo del broker titular (viñeta); null con el storage mock (stub://). */
  readonly logoInmobiliaria = computed(() => {
    const contacto = this.propiedad()?.contacto;
    return contacto && esUrlRenderizable(contacto.logoUrl)
      ? contacto.logoUrl!
      : null;
  });

  readonly nombreInmobiliaria = computed(() =>
    textoODefault(this.propiedad()?.contacto?.inmobiliaria),
  );

  // ── Visor a pantalla completa (botón icon-only del Figma `244:3078`) ───────

  /**
   * Foto que está mirando el visor, o `null` si está cerrado.
   */
  readonly visorIndice = signal<number | null>(null);

  /** A pantalla completa sí se justifica `full` (1920 px). */
  readonly fuentesAmpliada = computed<FuentesFoto | null>(() => {
    const indice = this.visorIndice();
    const foto = indice === null ? null : (this.galeria()[indice] ?? null);
    return foto ? fuentesFoto(foto, 'full') : null;
  });

  /**
   * Teclado de los overlays: ← → recorren la galería y Escape cierra.
   */
  tecla(evento: KeyboardEvent): void {
    if (this.visorIndice() !== null) {
      if (evento.key === 'Escape') this.cerrarFotoAmpliada();
      else if (evento.key === 'ArrowRight') this.fotoSiguiente();
      else if (evento.key === 'ArrowLeft') this.fotoAnterior();
      else return;
      evento.preventDefault();
    } else if (this.videoAbierto() && evento.key === 'Escape') {
      this.cerrarVideo();
    }
  }

  /** Abre el visor en la foto que se está viendo en grande. */
  ampliarFoto(): void {
    this.visorIndice.set(this.fotoActiva());
  }

  cerrarFotoAmpliada(): void {
    this.visorIndice.set(null);
  }

  /** El carrusel del visor recorre la galería completa y da la vuelta. */
  fotoSiguiente(): void {
    this.moverVisor(1);
  }

  fotoAnterior(): void {
    this.moverVisor(-1);
  }

  /**
   * Dejó de ser `private` con **#15**: además de las flechas y del teclado, lo
   * llama el `(swipe)` de la plantilla.
   */
  moverVisor(paso: 1 | -1): void {
    const total = this.galeria().length;
    if (total < 2) return;
    this.visorIndice.update((i) =>
      i === null ? i : (i + paso + total) % total,
    );
  }

  // ── Video de YouTube en lightbox (Figma node 22-1215) ──────────────────────

  readonly videoAbierto = signal(false);

  /**
   * URL embebible del video de YouTube (watch / youtu.be / shorts → embed),
   * marcada como confiable para el iframe. Null si no se puede embeber.
   */
  readonly videoEmbedUrl = computed<SafeResourceUrl | null>(() => {
    const url = this.propiedad()?.youtube;
    if (!url) return null;
    const id = /(?:youtube\.com\/(?:watch\?.*v=|shorts\/|embed\/)|youtu\.be\/)([\w-]{6,})/.exec(
      url,
    )?.[1];
    if (!id) return null;
    return this.sanitizer.bypassSecurityTrustResourceUrl(
      `https://www.youtube.com/embed/${id}?autoplay=1&rel=0`,
    );
  });

  /** Abre el lightbox; si la URL no es embebible, abre YouTube aparte. */
  verVideo(): void {
    if (this.videoEmbedUrl()) {
      this.videoAbierto.set(true);
    } else if (this.propiedad()?.youtube) {
      window.open(this.propiedad()!.youtube, '_blank', 'noopener');
    }
  }

  cerrarVideo(): void {
    this.videoAbierto.set(false);
  }

  /**
   * DP07 (flujo de retorno a fondo): query params con los que se vuelve al
   * buscador. `volverA` trae la URL completa del buscador en el momento de
   * entrar a la ficha — filtros, vista, cámara del mapa y pin seleccionado —
   * así el retorno restaura la pantalla exactamente como estaba.
   */
  queryRetorno: Params = {};

  constructor() {
    // `getCurrentNavigation()` y no `lastSuccessfulNavigation`: el componente
    // se construye DURANTE la activación de la ruta, así que en este momento
    // la última navegación exitosa todavía es la anterior (la del buscador) y
    // su state no tiene nada de esto. Con `lastSuccessfulNavigation` no había
    // vista previa y el link de Volver perdía los query params (DP07).
    const state = this.router.getCurrentNavigation()?.extras.state as
      | { tarjeta?: PropiedadPublica; fotos?: ImagenPublica[]; volverA?: string }
      | undefined;
    if (state?.tarjeta) this.tarjetaPrevia.set(state.tarjeta);
    if (state?.fotos?.length) this.fotosPrevias.set(state.fotos);
    if (state?.volverA?.startsWith('/buscador')) {
      this.queryRetorno = this.parsearQuery(state.volverA);
    }

    // Metadatos del preview al compartir: se cargan con el detalle,
    // que el render del servidor espera, y se restablecen al salir.
    inject(DestroyRef).onDestroy(() => this.openGraph.restablecer());

    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.service.detalle(id).subscribe({
        next: (detalle) => {
          this.detalle.set(detalle);
          this.cargando.set(false);
          this.openGraph.aplicar(this.tituloCompartir(), detalle.descripcion);
        },
        error: (error: unknown) => {
          this.cargando.set(false);
          if (
            this.responseInit &&
            error instanceof HttpErrorResponse &&
            STATUS_FICHA_INEXISTENTE.has(error.status)
          ) {
            this.responseInit.status = 404;
          }
        },
      });
    } else {
      this.cargando.set(false);
    }
  }

  /**
   * Convierte el query string de `volverA` en un objeto de query params,
   * preservando claves repetidas (multi-zona: varios `localidad`).
   */
  private parsearQuery(url: string): Params {
    const idx = url.indexOf('?');
    if (idx < 0) return {};
    const sp = new URLSearchParams(url.slice(idx + 1));
    const params: Params = {};
    sp.forEach((_, clave) => {
      const valores = sp.getAll(clave);
      params[clave] = valores.length > 1 ? valores : valores[0];
    });
    return params;
  }

  verFoto(indice: number): void {
    this.fotoActiva.set(indice);
  }

  /**
   * Pasa de foto en la galería con el dedo (#15). Da la vuelta en los extremos,
   * igual que el carrusel de la tarjeta del listado (RP-11): los puntos ya
   * dejan saltar a cualquier foto, así que el swipe no tiene por qué frenarse
   * en la última.
   */
  moverFoto(delta: 1 | -1): void {
    this.fotoActiva.update((i) =>
      indiceConWrap(i, delta, this.galeria().length),
    );
  }

  /**
   * Descarga la ficha A4 (FP-25): imprime la hoja `app-ficha-pdf` — lo único
   * visible al imprimir — y el diálogo del navegador la guarda como PDF.
   */
  async descargar(): Promise<void> {
    // `window.print()` es sincrónico: una foto a medio decodificar saldría
    // en blanco, así que se espera a que las de la hoja estén listas.
    const fotos = Array.from(
      document.querySelectorAll<HTMLImageElement>('app-ficha-pdf img'),
    );
    await Promise.allSettled(fotos.map((foto) => foto.decode()));
    window.print();
  }

  /** Comparte el aviso (Web Share API, con copia al portapapeles de respaldo). */
  readonly compartido = signal(false);

  async compartir(): Promise<void> {
    const data = {
      title: this.tituloCompartir(),
      url: location.href,
    };
    try {
      if (navigator.share) {
        await navigator.share(data);
      } else {
        await navigator.clipboard.writeText(location.href);
        this.compartido.set(true);
        setTimeout(() => this.compartido.set(false), 2000);
      }
    } catch {
      // Compartir cancelado por el usuario
    }
  }
}
